import React, {useState} from 'react'
import {createPortal} from 'react-dom'
import dayjs from 'dayjs'
import {useDispatch, useSelector} from 'react-redux'
import {useAuth0} from '@auth0/auth0-react'
import {toast} from 'react-toastify'
import {useAuthClient} from '../../../../utils/use-auth-client'
import {useUserFeatures} from '../../../../context/user-features-context'
import {
  normalizeKeapPartnerId,
  resolveKeapPartnerId,
} from '../../../../utils/resolveKeapPartnerId'
import {
  selectAccountInfo,
  selectLocations,
  selectOrderMode,
  selectSelectedLocationId,
  selectOrderId,
  selectSalesRepId,
  selectUserRole,
  selectSalesAttributionFields,
  selectIsUpgrade,
  selectProductConfigInfo,
} from '../../../../app-store/PurchaseLinkSlice'
import {
  selectEffectiveRole,
  selectPartnerAC,
} from '../../../../app-store/selector'
import {setKeapPartnerId} from '../../../../app-store/userSlice'
import {selectedInfluencerName} from '../../../../app-store/kpGlobalStateSlice'
import {
  buildSendLinkPayload,
  resolveContactName,
} from '../utils/buildSendLinkPayload'
import {
  buildPartnerGenerateLinkData,
  markOwnedCenters,
} from '../utils/buildPartnerGenerateLinkPayload'
import {getRelevantLocations} from '../utils/buildCreateOrderPayload'
import {buildLocationOrderSummary} from '../../../../pages/CatalogSelection/utils/orderSummaryUtils'
import {usePaymentLinkStatus} from '../hooks/usePaymentLinkStatus'
import {Button} from '../../../shared/Button'
import {GeneratedLinkModal} from './GeneratedLinkModal'
import {GeneratingLinkModal} from './GeneratingLinkModal'
import Icon from '../../../../core/shared/Icon'
import {theme} from '../../../theme'
import {
  StepCardWrapper,
  StepCard,
  FooterRow,
  FooterCenter,
} from '../AccountSetup.styles'
import {
  StyledMailIcon,
  StyledTelIcon,
  StyledLinkIcon,
} from '../../../shared/icons/icons.styles'
import {PaymentDetailsOrderSummary} from './PaymentDetailsOrderSummary'
import {
  Layout,
  MainColumn,
  StepRootWrapper,
  StatusOverlay,
  PaymentStatusDock,
  PageHeading,
  HeadingTitle,
  HeadingSubtitle,
  BillToPanel,
  BillToRow,
  BillToRowActions,
  BillToDetails,
  BillToSummary,
  BillToSummaryLine,
  BillToSummaryLabel,
  BillToSummaryName,
  BillToSummaryAddress,
  LocationCardList,
  LocationCard,
  LocationCardRow,
  LocationCardMainIcon,
  LocationCardDetailsCol,
  LocationCardTopRow,
  LocationCardIndexLabel,
  LocationCardNewItems,
  LocationCardName,
  LocationCardPay,
  LocationCardPayAmount,
  LocationCardPayMonthly,
  TwoColumnRow,
  FullWidthCell,
  SendViaFieldGroup,
  FieldLabel,
  SendViaToggle,
  SendViaOption,
  SendPaymentLinkButton,
  SendLinkButtonGroup,
  StatusPanel,
  StatusPanelHeader,
  StatusHeaderLeft,
  StatusIconBadge,
  StatusHeaderTextGroup,
  StatusHeaderLabel,
  StatusHeaderEmail,
  ExpiresPill,
  ExpiresLabel,
  ExpiresValue,
  StatusDivider,
  PaymentStatusSection,
  EmptyStatusCard,
  TimelineList,
  TimelineItem,
  TimelineDot,
  TimelineBody,
  TimelineLabel,
  TimelineTimestamp,
  TimelineDescription,
  ShareLinkRow,
  ShareLinkInput,
  CollapseTab,
  CollapseTabChevron,
  CollapseTabLabel,
  StatusPanelContent,
} from './PaymentDetailsStep.styles'

const STEP_DESCRIPTIONS = {
  1: 'We shared a secure payment link with the client.',
  2: 'The client reviewed the payment details.',
  3: 'The client has added their payment method.',
  4: 'This step will complete once payment is confirmed.',
}

function formatCountdown(expiresAt) {
  const remainingMs = expiresAt - Date.now()
  if (remainingMs <= 0) return '00:00:00'
  const totalSeconds = Math.floor(remainingMs / 1000)
  const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0')
  const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(
    2,
    '0',
  )
  const seconds = String(totalSeconds % 60).padStart(2, '0')
  return `${hours}:${minutes}:${seconds}`
}

function useCountdownDisplay(expiresAt) {
  const [, forceTick] = useState(0)
  React.useEffect(() => {
    if (!expiresAt) return
    const interval = setInterval(() => forceTick(t => t + 1), 1000)
    return () => clearInterval(interval)
  }, [expiresAt])
  return expiresAt ? formatCountdown(expiresAt) : null
}

function formatStepTimestamp(completedAt) {
  return dayjs(completedAt).format('MMM D · hh:mm A')
}

function formatAmount(amount) {
  return `$${Number(amount || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

// The real Payment Details page — reached from Catalog Selection's Continue
// button (directly, or after Scheduling when the cart needs an onboarding
// call). Supports all three PurchaseLinkSlice orderModes:
//   - Single location & Multi-location Self Pay: both are orderMode
//     'location' — the account may have many locations, but only the active
//     one is part of THIS order (see getRelevantLocations), so both render
//     identically here (no location list, one link for the one location).
//   - Multi-location Parent Pay: orderMode 'parentPay' — every parent-pay
//     location was already folded into ONE order_header at Catalog Continue
//     (buildCreateOrderPayload's relevantLocations), so "Send Payment Link"
//     sends ONE combined link for the whole group, matching the confirmed
//     "one link for all the parentpay locations" design.
export function PaymentDetailsStep({onCancel, onExitOrder}) {
  const client = useAuthClient()
  const dispatch = useDispatch()
  const {user} = useAuth0()
  const userFeatures = useUserFeatures()
  const showCopyLinkButton = !!userFeatures?.Payment_Link_Copy_Button
  const accountInfo = useSelector(selectAccountInfo)
  const locations = useSelector(selectLocations)
  const orderMode = useSelector(selectOrderMode)
  const selectedLocationId = useSelector(selectSelectedLocationId)
  const orderId = useSelector(selectOrderId)
  const salesRepId = useSelector(selectSalesRepId)
  const userRole = useSelector(selectUserRole)
  const salesAttributionFields = useSelector(selectSalesAttributionFields)
  const isUpgrade = useSelector(selectIsUpgrade)
  const productConfigInfo = useSelector(selectProductConfigInfo)
  const effectiveRole = useSelector(selectEffectiveRole)
  const partnerAffiliateCode = useSelector(selectPartnerAC)
  const storedKeapPartnerId = useSelector(state => state.user.keapPartnerId)

  const isInfluencerOps = effectiveRole.includes('InfluencerOps')
  // Captured on the Catalog Selection step (same store field the 1:many flow uses).
  const influencerName = (useSelector(selectedInfluencerName) ?? '').trim()
  const isPartnerOps = effectiveRole.includes('PartnerOps')
  const isKeapPartner = effectiveRole.includes('KeapPartner')
  const usesPartnerGenerateLink =
    isInfluencerOps || isPartnerOps || isKeapPartner

  const isParentPay = orderMode === 'parentPay'
  const relevantLocations = getRelevantLocations({
    locations,
    orderMode,
    selectedLocationId,
  })
  // Mirrors buildSendLinkPayload's `identity` — a combined parentPay order
  // bills to (and displays) the parent account; a single-location order
  // bills to that one location, so its own accountInfo (set by
  // buildPurchaseLinkState) is what the rep should see here, not the parent
  // it hangs off of.
  const identity = isParentPay
    ? accountInfo
    : relevantLocations[0]?.accountInfo ?? accountInfo
  // Same name send-link will use (see resolveContactName): a combined order's
  // parent has no first/last name of its own, so this falls back to a location's.
  const contactName = resolveContactName(identity, relevantLocations)
  // Due-today/monthly totals per location for the "Pay - $X + $Y/mo" line —
  // total/monthlyTotal don't depend on isMonthlyTab (see
  // buildLocationOrderSummary), so no tab option is needed here.
  const locationSummaries = relevantLocations.map(loc =>
    buildLocationOrderSummary(loc.items),
  )

  const [sendLinkVia, setSendLinkVia] = useState('email')
  const [isSending, setIsSending] = useState(false)
  // Only the Keap Partner Generate Link action — isSending is shared with the
  // Send / Send Again payment-link actions, which keep their button spinner.
  const [isGeneratingPartnerLink, setIsGeneratingPartnerLink] = useState(false)
  const [sentLink, setSentLink] = useState(null) // {paymentUuid, shortenedUrl, sentVia}
  const [generatedPartnerLink, setGeneratedPartnerLink] = useState('')
  const [isGeneratedLinkModalOpen, setIsGeneratedLinkModalOpen] =
    useState(false)
  const [statusCollapsed, setStatusCollapsed] = useState(true)
  const [linkCopied, setLinkCopied] = useState(false)

  const handleCopyPaymentLink = () => {
    navigator.clipboard.writeText(sentLink.shortenedUrl)
    setLinkCopied(true)
    toast.success('Link copied.')
    setTimeout(() => setLinkCopied(false), 2000)
  }

  const sentLinkContact =
    sentLink?.sentVia === 'sms'
      ? identity?.mobile || identity?.phone
      : identity?.email

  // Lock background scroll while the full-height drawer is open — restored
  // on close and on unmount so navigating away mid-drawer never leaves the
  // page stuck unscrollable.
  React.useEffect(() => {
    if (statusCollapsed) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [statusCollapsed])

  const {timeline, expiresAt} = usePaymentLinkStatus(sentLink?.paymentUuid)
  const countdownDisplay = useCountdownDisplay(expiresAt)
  const firstIncompleteIndex = timeline.findIndex(step => !step.completedAt)
  // Order Confirmation (step 4) completing means payment is done and the
  // order is complete — the link has served its purpose, so resending it
  // no longer makes sense.
  const isOrderComplete = !!timeline.find(step => step.step_number === 4)
    ?.completedAt
  const hasAppointments = relevantLocations.some(
    loc => loc.onboardingApptId || loc.thryvSuccessServicesApptId,
  )
  const isLinkExpired = !!expiresAt && countdownDisplay === '00:00:00'

  const country = identity?.country || 'US'

  const handleSendPaymentLink = async () => {
    setIsSending(true)
    try {
      const payload = buildSendLinkPayload({
        accountInfo,
        locations,
        orderMode,
        selectedLocationId,
        orderId,
        sendLinkVia,
        salesRepId,
        userRole,
        salesAttributionFields,
        productConfigInfo,
      })
      const response = await client('cc/order/send-link', {
        data: payload,
        method: 'POST',
      })
      setSentLink({
        paymentUuid: response.paymentUuid,
        shortenedUrl: response.shortenedUrl,
        sentVia: sendLinkVia,
      })
      toast.success(response.message || 'Payment link sent.')

      // Mirrors the legacy flow's updateOrder5Status('send-link', 'Submitted', ...)
      // call in new-order.js — the link already went out above, so a failure here
      // shouldn't undo that success toast or block the rep; just log it.
      try {
        await client('ordercapture/services/updateOrderStatus', {
          data: {
            order_id: orderId,
            order_status: 'Submitted',
            payment_uuid: response.paymentUuid,
          },
          method: 'PUT',
        })
      } catch (statusErr) {
        console.error('Unable to update order status to Submitted:', statusErr)
      }
    } catch (err) {
      toast.error(err?.message || 'Unable to send the payment link.')
    } finally {
      setIsSending(false)
    }
  }

  const handleGeneratePartnerLink = async () => {
    setIsSending(true)
    setIsGeneratingPartnerLink(true)
    try {
      // PartnerOps isn't itself a Keap partner — the link is attributed to the
      // XP / Affiliate Code on the order (what the legacy partner-ops link used
      // as keapPartnerId), so that code wins over any session partner id.
      const partnerOpsXpCode = isPartnerOps
        ? salesAttributionFields?.xpCode || accountInfo?.xpCode
        : null
      let partnerId = resolveKeapPartnerId({
        isInfluencerOps,
        // A KeapPartner's links always belong to the logged-in partner, so the
        // list (which matches the session/profile id) shows every link they
        // create; pac / account ids only attribute PartnerOps links.
        overrides: isPartnerOps
          ? [partnerOpsXpCode, partnerAffiliateCode, accountInfo?.keapPartnerId]
          : [],
        storedKeapPartnerId,
        user,
      })

      // KP11 resolves partner details from /api/user when the Auth0 token is
      // missing partner metadata. Role selects the KP workflow, but the
      // generated link still needs the specific partner owner.
      if (!partnerId && user?.email) {
        const partnerDetails = await client(
          `user?email=${encodeURIComponent(user.email)}`,
        )
        const metadata = partnerDetails?.user_metadata ?? {}
        partnerId = normalizeKeapPartnerId(
          isInfluencerOps ? metadata.networkId : metadata.keapPartnerId,
        )
        if (partnerId) dispatch(setKeapPartnerId(partnerId))
      }

      if (!partnerId) {
        throw new Error('Keap Partner ID is missing for this session.')
      }

      const partnerLinkData = buildPartnerGenerateLinkData({
        locations,
        orderMode,
        selectedLocationId,
        orderId,
        country,
        keapPartnerId: partnerId,
        partnerEmail: user?.email,
        productConfigInfo,
        couponCodes: isInfluencerOps ? [] : ['keap-partner-waive-obfee'],
      })
      // Every Core Account Setup link (including one-location accounts) needs
      // the location-tagged send-link payload persisted with the KP link.
      // OOECP uses it to build the payment order and summary; the legacy
      // planCode fields alone contain neither a payment UUID nor the complete
      // Core subscription grouping.
      partnerLinkData.corePaymentPayload = markOwnedCenters(
        buildSendLinkPayload({
          accountInfo,
          locations,
          orderMode,
          selectedLocationId,
          orderId,
          salesRepId,
          userRole,
          salesAttributionFields,
          // Restores each Keap add-on's parentProduct so Additional Contacts /
          // Text & Voice nest under the Keap Add-Ons plan with their tier quantity.
          productConfigInfo,
        }),
        {locations, orderMode, selectedLocationId},
      )

      // KP11's current Generate Link action uses this endpoint for
      // KeapPartner, InfluencerOps, and PartnerOps alike. Keep the new Core
      // catalog handoff on that established contract rather than sending any
      // of those roles through the standard emailed-payment flow.
      const response = await client('cc/order/keap-partner/generate-link', {
        data: {
          ...partnerLinkData,
          sfPartnerAccountId:
            user?.user_metadata?.sfPartnerAccountId || '123121',
          ...(isInfluencerOps ? {influencerName} : {}),
        },
        method: 'POST',
      })

      if (!response?.generatedUrl) {
        throw new Error('The partner link could not be generated.')
      }

      // Replace the creation-time link_id: 0 placeholder with the actual
      // KP link id, mirroring KP11's Generate Link post-processing so the
      // account remains visible in the partner's book of business.
      if (response.linkId && identity?.email) {
        try {
          await client('cc/order/keapClient/createClientPartnerMap', {
            data: {
              link_id: response.linkId,
              keap_partner_id: String(partnerId),
              keap_partner_email: user?.email,
              keap_client_email: identity.email,
              status: 'Pending',
            },
            method: 'POST',
          })
        } catch (mapError) {
          // The link itself is already valid; preserve legacy behavior by
          // treating bookkeeping failure as non-fatal.
          console.error('Unable to update Keap client-partner map:', mapError)
        }
      }
      setGeneratedPartnerLink(response.generatedUrl)
      setIsGeneratedLinkModalOpen(true)
      toast.success('Purchase link generated.')
    } catch (err) {
      toast.error(err?.message || 'Unable to generate the purchase link.')
    } finally {
      setIsGeneratingPartnerLink(false)
      setIsSending(false)
    }
  }

  // Mirrors the legacy payment-details.js "Send Again" behavior: same
  // order/contact info, but the rep can switch channel first. The backend
  // (cc/order/resend-link) issues a brand-new payment_uuid, so simply
  // overwriting `sentLink` here is enough to reset the tracker below —
  // usePaymentLinkStatus is keyed off sentLink.paymentUuid, so a new uuid
  // naturally starts its poll/socket join from an empty timeline.
  const handleResendPaymentLink = async () => {
    setIsSending(true)
    try {
      const payload = {
        orderId: orderId != null ? String(orderId) : undefined,
        orderHeaderId: orderId,
        paymentUuid: sentLink.paymentUuid,
        method: sendLinkVia,
        country,
        appUrl: window.location.origin,
        ...(sendLinkVia === 'sms'
          ? {contactPhone: identity?.mobile || identity?.phone || ''}
          : {email: identity?.email || ''}),
      }
      const response = await client('cc/order/resend-link', {
        data: payload,
        method: 'PATCH',
      })
      setSentLink({
        paymentUuid: response.newUuid,
        shortenedUrl: response.shortenedUrl,
        sentVia: sendLinkVia,
      })
      toast.success('Payment link resent.')
    } catch (err) {
      toast.error(err?.message || 'Unable to resend the payment link.')
    } finally {
      setIsSending(false)
    }
  }

  return (
    <>
      <StepRootWrapper>
        {!statusCollapsed && <StatusOverlay />}

        <PageHeading>
          <HeadingTitle>
            {isParentPay
              ? `Payment Link Generated for ${
                  relevantLocations.length
                } Location${relevantLocations.length === 1 ? '' : 's'}`
              : 'Payment Details'}
          </HeadingTitle>
          <HeadingSubtitle>
            Order #{orderId ?? 'unknown'} for{' '}
            {identity?.businessName || 'this account'}
          </HeadingSubtitle>
        </PageHeading>

        <StepCardWrapper blurred={!statusCollapsed}>
          <StepCard noPadding>
            <Layout>
              <MainColumn>
                <BillToPanel>
                  <BillToRow>
                    <BillToDetails>
                      <BillToSummary>
                        <BillToSummaryLine>
                          <BillToSummaryLabel>
                            Send Invoice to:
                          </BillToSummaryLabel>
                          {identity?.email || '—'}
                        </BillToSummaryLine>
                        <BillToSummaryLine>
                          <BillToSummaryLabel>Bill To:</BillToSummaryLabel>
                          <BillToSummaryName>
                            {identity?.businessName || '—'}
                          </BillToSummaryName>
                          {(identity?.city || identity?.state) && (
                            <BillToSummaryAddress>
                              {[identity?.city, identity?.state]
                                .filter(Boolean)
                                .join(', ')}
                            </BillToSummaryAddress>
                          )}
                        </BillToSummaryLine>
                      </BillToSummary>

                      <BillToSummary>
                        <BillToSummaryLine>
                          <BillToSummaryLabel>Contact:</BillToSummaryLabel>
                          {[contactName.firstName, contactName.lastName]
                            .filter(Boolean)
                            .join(' ') || '—'}
                        </BillToSummaryLine>
                        <BillToSummaryLine>
                          <BillToSummaryLabel>Phone:</BillToSummaryLabel>
                          {identity?.mobile || identity?.phone || '—'}
                        </BillToSummaryLine>
                      </BillToSummary>
                    </BillToDetails>

                    <BillToRowActions>
                      {!usesPartnerGenerateLink && (
                        <SendViaFieldGroup>
                          <FieldLabel>Send Payment Link Via</FieldLabel>
                          <SendViaToggle>
                            <SendViaOption
                              type="button"
                              active={sendLinkVia === 'email'}
                              disabled={isSending}
                              onClick={() => setSendLinkVia('email')}
                            >
                              <StyledMailIcon />
                              Email
                            </SendViaOption>
                            <SendViaOption
                              type="button"
                              active={sendLinkVia === 'sms'}
                              disabled={isSending}
                              onClick={() => setSendLinkVia('sms')}
                            >
                              <StyledTelIcon />
                              Text Message
                            </SendViaOption>
                          </SendViaToggle>
                        </SendViaFieldGroup>
                      )}

                      <SendLinkButtonGroup>
                        {usesPartnerGenerateLink ? (
                          <SendPaymentLinkButton
                            variant="solid-gradiant"
                            size="sm"
                            isLoading={isSending}
                            disabled={
                              !!generatedPartnerLink ||
                              (isInfluencerOps && !influencerName)
                            }
                            onClick={handleGeneratePartnerLink}
                            leftIcon={<StyledLinkIcon />}
                          >
                            {generatedPartnerLink
                              ? 'Generate New Link'
                              : 'Generate Link'}
                          </SendPaymentLinkButton>
                        ) : sentLink ? (
                          <SendPaymentLinkButton
                            variant="solid-dark-blue"
                            size="sm"
                            isLoading={isSending}
                            disabled={
                              isOrderComplete ||
                              (hasAppointments && isLinkExpired)
                            }
                            onClick={handleResendPaymentLink}
                            leftIcon={<Icon name="send" size="sm" />}
                            title={
                              isOrderComplete
                                ? 'Order is already complete'
                                : hasAppointments && isLinkExpired
                                ? 'Payment link has expired'
                                : undefined
                            }
                          >
                            Send Again
                          </SendPaymentLinkButton>
                        ) : (
                          <SendPaymentLinkButton
                            variant="solid-dark-blue"
                            size="sm"
                            isLoading={isSending}
                            onClick={handleSendPaymentLink}
                            leftIcon={<Icon name="send" size="sm" />}
                          >
                            Send Payment Link
                          </SendPaymentLinkButton>
                        )}
                      </SendLinkButtonGroup>
                    </BillToRowActions>
                  </BillToRow>
                </BillToPanel>

                <TwoColumnRow>
                  {isParentPay && (
                    <LocationCardList>
                      {relevantLocations.map((loc, index) => {
                        const summary = locationSummaries[index]
                        return (
                          <LocationCard key={loc.locationId}>
                            <LocationCardRow>
                              <LocationCardMainIcon>
                                <Icon
                                  name="location-main"
                                  size="sm"
                                  style={{width: 12, height: 12}}
                                />
                              </LocationCardMainIcon>
                              <LocationCardDetailsCol>
                                <LocationCardTopRow>
                                  <LocationCardIndexLabel>
                                    <Icon name="loc-ind" size="xxs" />
                                    Location {index + 1}
                                  </LocationCardIndexLabel>
                                  <LocationCardNewItems>
                                    {summary.newItemCount} new item(s)
                                  </LocationCardNewItems>
                                </LocationCardTopRow>
                                <LocationCardName>
                                  {loc.locationName || loc.thryvId}
                                </LocationCardName>
                                <LocationCardPay>
                                  <Icon
                                    name="fwdArrow"
                                    size="xxs"
                                    color={theme.colors.text.primary}
                                  />
                                  <LocationCardPayAmount>
                                    Pay - {formatAmount(summary.total)}
                                  </LocationCardPayAmount>
                                  <LocationCardPayMonthly>
                                    + {formatAmount(summary.monthlyTotal)}/mo
                                  </LocationCardPayMonthly>
                                </LocationCardPay>
                              </LocationCardDetailsCol>
                            </LocationCardRow>
                          </LocationCard>
                        )
                      })}
                    </LocationCardList>
                  )}

                  {isParentPay ? (
                    <PaymentDetailsOrderSummary
                      locations={relevantLocations}
                      orderMode={orderMode}
                      country={country}
                      isUpgrade={isUpgrade}
                    />
                  ) : (
                    <FullWidthCell>
                      <PaymentDetailsOrderSummary
                        locations={relevantLocations}
                        orderMode={orderMode}
                        country={country}
                        isUpgrade={isUpgrade}
                      />
                    </FullWidthCell>
                  )}
                </TwoColumnRow>
              </MainColumn>
            </Layout>
          </StepCard>

          {/* Once a link is out (sent, or generated for partner roles) the
          order must not be cancelled — Exit just resets and navigates. */}
          <PaymentDetailsStepFooter
            onCancel={onCancel}
            onExit={onExitOrder}
            isLinkSent={!!sentLink || !!generatedPartnerLink}
          />
        </StepCardWrapper>
      </StepRootWrapper>

      {/* Outside StepCardWrapper (not a descendant of it) so its blur when
      the drawer is open never applies to the drawer/tab themselves.
      Portaled straight to document.body — see PaymentStatusDock's comment
      in PaymentDetailsStep.styles.js for why: the standalone host's <main>
      traps z-index in its own stacking context, so rendered in place this
      dock could never out-rank the app header no matter its own z-index. */}
      {/* Partner roles only generate a link (no sentLink), so there is nothing
      to track — hide the dock for now (FULSAS-14461). */}
      {!usesPartnerGenerateLink &&
        createPortal(
          <PaymentStatusDock open={!statusCollapsed}>
            <CollapseTab
              type="button"
              expanded={!statusCollapsed}
              onClick={() => setStatusCollapsed(collapsed => !collapsed)}
              aria-label={
                statusCollapsed
                  ? 'Expand payment status'
                  : 'Collapse payment status'
              }
              title={
                statusCollapsed
                  ? 'Expand payment status'
                  : 'Collapse payment status'
              }
            >
              <CollapseTabChevron>
                {statusCollapsed ? '‹' : '›'}
              </CollapseTabChevron>
              <CollapseTabLabel>Payment Status</CollapseTabLabel>
            </CollapseTab>

            <StatusPanel>
              <StatusPanelContent>
                <StatusPanelHeader>
                  <StatusHeaderLeft>
                    <StatusIconBadge>
                      <StyledLinkIcon />
                    </StatusIconBadge>
                    <StatusHeaderTextGroup>
                      <StatusHeaderLabel>
                        {sentLink ? 'Payment Link Sent To' : 'Payment Status'}
                      </StatusHeaderLabel>
                      {sentLink && sentLinkContact && (
                        <StatusHeaderEmail>{sentLinkContact}</StatusHeaderEmail>
                      )}
                    </StatusHeaderTextGroup>
                  </StatusHeaderLeft>
                  {countdownDisplay && (
                    <ExpiresPill>
                      <ExpiresLabel>Expires in</ExpiresLabel>
                      <ExpiresValue>{countdownDisplay}</ExpiresValue>
                    </ExpiresPill>
                  )}
                </StatusPanelHeader>

                {sentLink ? (
                  <>
                    <StatusDivider />

                    <PaymentStatusSection>
                      <StatusHeaderLabel>Payment Status</StatusHeaderLabel>
                      <TimelineList>
                        {timeline.map((step, i) => {
                          const state = step.completedAt
                            ? 'done'
                            : i === firstIncompleteIndex
                            ? 'current'
                            : 'upcoming'
                          return (
                            <TimelineItem key={step.step_number} state={state}>
                              <TimelineDot state={state}>
                                {state === 'done' ? '✓' : ''}
                              </TimelineDot>
                              <TimelineBody>
                                <TimelineLabel state={state}>
                                  {step.label}
                                </TimelineLabel>
                                <TimelineTimestamp>
                                  {step.completedAt
                                    ? formatStepTimestamp(step.completedAt)
                                    : 'Pending'}
                                </TimelineTimestamp>
                                {STEP_DESCRIPTIONS[step.step_number] && (
                                  <TimelineDescription>
                                    {STEP_DESCRIPTIONS[step.step_number]}
                                  </TimelineDescription>
                                )}
                              </TimelineBody>
                            </TimelineItem>
                          )
                        })}
                      </TimelineList>
                    </PaymentStatusSection>

                    {sentLink.shortenedUrl && showCopyLinkButton && (
                      <>
                        <StatusDivider />
                        <ShareLinkRow>
                          <ShareLinkInput
                            readOnly
                            value={sentLink.shortenedUrl}
                            onFocus={e => e.target.select()}
                          />
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleCopyPaymentLink}
                            leftIcon={<Icon name="copy" size="sm" />}
                          >
                            {linkCopied ? 'Copied!' : 'Copy Link'}
                          </Button>
                        </ShareLinkRow>
                      </>
                    )}
                  </>
                ) : (
                  <EmptyStatusCard>
                    <StatusIconBadge>
                      <StyledLinkIcon />
                    </StatusIconBadge>
                    <span>
                      Send the payment link to start tracking its progress here.
                    </span>
                  </EmptyStatusCard>
                )}
              </StatusPanelContent>
            </StatusPanel>
          </PaymentStatusDock>,
          document.body,
        )}

      <GeneratingLinkModal isOpen={isGeneratingPartnerLink} />

      {isGeneratedLinkModalOpen && (
        <GeneratedLinkModal
          link={generatedPartnerLink}
          onClose={() => setIsGeneratedLinkModalOpen(false)}
        />
      )}
    </>
  )
}

// This step's own footer card — a sibling below StepCard (see
// AccountSetup/index.js), not inside it. Send Payment Link now lives in the
// Bill To row's third column instead of here (see BillToRow/
// SendLinkButtonGroup above), so this footer only carries Cancel.
export function PaymentDetailsStepFooter({onCancel, onExit, isLinkSent}) {
  return (
    <FooterRow>
      <FooterCenter>
        <Button
          variant="secondary-gray"
          size="sm"
          onClick={isLinkSent && onExit ? onExit : onCancel}
        >
          {isLinkSent ? 'Exit' : 'Cancel'}
        </Button>
      </FooterCenter>
    </FooterRow>
  )
}
